import {
    LibraryBooksOutlined as DownloadManualIcon,
    Logout as LogoutIcon,
    Person as PersonIcon,
    SettingsRounded as SettingsRoundedIcon,
} from '@mui/icons-material';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import is from '@sindresorhus/is';
import { Fragment, useContext, useState } from 'react';

import { ApplicationContext } from '../../../../shared/source/context/context';
import { AvatarSmall } from '../../design/components/avatar/AvatarSmall';
import { IncognitoIcon } from '../../design/icons/IncognitoIcon';
import { getUserName } from '../common/user';
import { route as profilePageRoute } from '../pages/profile/constants';
import { route } from '../pages/settings/constants';
import { route as settingsPageRoute } from '../pages/settings/constants';
import { PrivacyTermsDialog } from '../pages/settings/Dialogs';

import type { FunctionComponent, MouseEventHandler } from 'react';

const AnonymizeMenuItem: FunctionComponent = () => {
    const { actions, applicationState, translate } = useContext(ApplicationContext);
    const { userIsAnonymized } = applicationState;

    const itemText = translate(
        userIsAnonymized
            ? 'components.anonymize-button.user-names-show'
            : 'components.anonymize-button.user-names-hide',
    );
    const sx = {
        color: userIsAnonymized ? 'primary.main' : undefined,
    };

    return (
        <MenuItem
            aria-label={translate('usermenu.anonymize')}
            onClick={() => {
                actions.toggleAnonymization();
            }}
        >
            <ListItemIcon sx={sx}>
                <IncognitoIcon />
            </ListItemIcon>
            <ListItemText sx={sx}>{itemText}</ListItemText>
        </MenuItem>
    );
};

const privacyStatementDialogSearchString = new URLSearchParams({
    [route.searchParams.openPrivacyStatement]: 'true',
}).toString();

export const UserMenu: FunctionComponent = () => {
    const { actions, applicationState, translate } = useContext(ApplicationContext);
    const { myUserProfile } = applicationState;
    const theme = useTheme();
    const fullName = getUserName(myUserProfile);
    const userManualUrlHref = applicationState.customization.get('uiConfiguration').get('userManualUrl').get('href');
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const canSeeHideNamesButton = myUserProfile
        .map((user) => user.roles?.includes('recruiter') === true || user.roles?.includes('hiring_manager') === true)
        .unwrapOr(false);

    const handleClick: MouseEventHandler<HTMLElement> = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (): void => {
        setAnchorEl(null);
    };

    const handleLogout = (): void => {
        actions.logout();
    };

    return (
        <Fragment>
            <PrivacyTermsDialog />
            <Tooltip title={translate('usermenu.tooltip')}>
                <Button
                    endIcon={
                        <AvatarSmall
                            firstName={myUserProfile.get('firstName').unwrapOr('')}
                            lastName={myUserProfile.get('lastName').unwrapOr('')}
                        />
                    }
                    variant="text"
                    onClick={handleClick}
                >
                    <Typography color={theme.palette.text.primary} noWrap={true} variant="body1">
                        {fullName}
                    </Typography>
                </Button>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={is.null(anchorEl) === false} onClick={handleClose} onClose={handleClose}>
                <MenuItem
                    aria-label={translate('usermenu.my-profile')}
                    onClick={() => actions.navigateTo(profilePageRoute.patterns.usersMe)}
                >
                    <ListItemIcon>
                        <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('usermenu.my-profile')}</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem
                    aria-label={translate('pages.settings.data-privacy.title')}
                    onClick={() => {
                        actions.navigateTo(`${location.pathname}?${privacyStatementDialogSearchString}`);
                    }}
                >
                    <ListItemIcon>
                        <VerifiedUserIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('pages.settings.data-privacy.title')}</ListItemText>
                </MenuItem>
                <MenuItem
                    aria-label={translate('common.imprint')}
                    onClick={() => {
                        globalThis.open('https://www.cobrainer.com/imprint', '_blank');
                    }}
                >
                    <ListItemIcon>
                        <TextSnippetIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('common.imprint')}</ListItemText>
                </MenuItem>
                {canSeeHideNamesButton === true && <AnonymizeMenuItem />}
                <MenuItem
                    aria-label={translate('usermenu.account-settings')}
                    onClick={() => actions.navigateTo(settingsPageRoute.patterns.settings)}
                >
                    <ListItemIcon>
                        <SettingsRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('usermenu.account-settings')}</ListItemText>
                </MenuItem>
                {userManualUrlHref.isJust && (
                    <Fragment>
                        <Divider />
                        <MenuItem
                            aria-label={translate('common.download-manual')}
                            onClick={() => {
                                globalThis.open(userManualUrlHref.value, '_blank');
                            }}
                        >
                            <ListItemIcon>
                                <DownloadManualIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{translate('common.download-manual')}</ListItemText>
                        </MenuItem>
                    </Fragment>
                )}
                <Divider />
                {!applicationState.hideLogoutButtons && (
                    <MenuItem aria-label={translate('common.logout')} onClick={handleLogout}>
                        <ListItemIcon>
                            <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{translate('common.logout')}</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </Fragment>
    );
};
